/**
 * A saved contract → which Data-step row each slot points at (fix/widgets).
 *
 * The builder now asks about every slot of the type, not only the named
 * columns of a record set, so reopening a widget has to answer the same
 * questions back. The service already resolves the **column** slots, because
 * it is the only place that still has the record set's alias → field uuid map
 * (`mapBindingsToSlotAssignments`). This resolves the other two pools, which
 * only exist as form rows:
 *
 * - **the row's own alias**, when the read carried one: an aggregation and a
 *   grouper travel under the alias the bindings point at, so `sum_amount` and
 *   `sum_quota` name their rows exactly and a crossed `value` / `target` pair
 *   comes back the way it was saved.
 * - `record_count` is the row count — the explicit `COUNT_ROWS` measure if the
 *   author added one, and the implicit one otherwise.
 * - `dim_N` is the Nth grouper, which is exactly how `grouperAlias` names it.
 * - anything else is `<aggregate>_<field code>`, as `aliasForMeasure` builds
 *   it, so the operation is read off the front of the key and the first
 *   measure row that still has it is taken. This last one is the fallback for
 *   a widget saved before the read kept the aliases.
 *
 * It reads the **keys**, not the type's schema: the catalog loads
 * asynchronously and the form's initial state cannot wait for it. The three
 * shapes above are the builder's own naming, and a column alias never takes
 * them (`buildColumnAliases` derives one from the field's code, and reserves
 * `record_count` and `record_uuid`).
 *
 * A key that resolves to nothing is left unanswered rather than guessed: the
 * slot then falls back to the automatic assignment, which is what produced the
 * binding in the first place, so the next save writes the same contract.
 */

import {
  AGGREGATE_WITHOUT_FIELD,
  NON_FIELD_BINDING_SLOTS,
  WIDGET_AGGREGATES,
} from '../constants/widgetContract.js';
import { ROW_COUNT_REF, grouperRef, measureRef } from './widgetSlotPools.js';

const EMPTY = Object.freeze({});

/** Longest first, so `count_distinct_x` is not read as a `COUNT`. */
const AGGREGATE_PREFIXES = [...WIDGET_AGGREGATES]
  .sort((a, b) => b.length - a.length)
  .map((aggregate) => [`${aggregate.toLowerCase()}_`, aggregate]);

const aggregateOf = (key) =>
  AGGREGATE_PREFIXES.find(([prefix]) => key.startsWith(prefix))?.[1] ?? '';

const keyOf = (item) => item?.field_key || item?.field || '';

/**
 * @param {object} params
 * @param {object|null} params.bindings The stored `visualization_schema.bindings`.
 * @param {Array} [params.measures] The hydrated measure rows, with their ids.
 * @param {Array} [params.groupers] The hydrated grouper rows, with their ids.
 * @returns {object} `{ [slotKey]: ref | ref[] }` for the measure and dimension
 *   slots it could resolve. Column slots are not touched.
 */
export function restoreSlotAssignments({ bindings, measures = [], groupers = [] }) {
  if (!bindings || typeof bindings !== 'object') return EMPTY;
  if (!measures.length && !groupers.length) return EMPTY;

  const spent = new Set();

  /** The row that travels under this exact alias, measure or grouper. */
  const byAlias = (key) => {
    const row = [...measures, ...groupers].find(
      (candidate) => candidate.alias === key && !spent.has(candidate.id),
    );
    if (!row) return '';
    spent.add(row.id);
    return row.id;
  };

  const refFor = (item) => {
    const key = keyOf(item);
    if (!key) return '';

    const exact = byAlias(key);
    if (exact) return exact;

    const at = /^dim_(\d+)$/.exec(key);
    if (at) {
      const index = Number(at[1]) - 1;
      const row = groupers[index];
      return row && !spent.has(row.id) ? grouperRef(row, index) : '';
    }

    if (key === ROW_COUNT_REF) {
      const row = measures.find((measure) => measure.aggregate === 'COUNT_ROWS'
        && !spent.has(measure.id));
      if (!row) return '';
      spent.add(row.id);
      return measureRef(row);
    }

    const aggregate = aggregateOf(key);
    if (!aggregate) return '';
    const row = measures.find((measure) => measure.aggregate === aggregate
      && !spent.has(measure.id));
    if (!row) return '';
    spent.add(row.id);
    return measureRef(row);
  };

  return Object.entries(bindings).reduce((acc, [slot, stored]) => {
    if (NON_FIELD_BINDING_SLOTS.includes(slot) || !stored) return acc;

    if (Array.isArray(stored)) {
      const refs = stored.map(refFor).filter(Boolean);
      return refs.length ? { ...acc, [slot]: refs } : acc;
    }

    const ref = refFor(stored);
    return ref ? { ...acc, [slot]: ref } : acc;
  }, EMPTY);
}

/**
 * A saved contract → the slot answers, for a widget built on a DATASET
 * (CT-5860).
 *
 * The inverse of `buildWidgetPayload`'s dataset branch, and the one that
 * matters now: since CT-5725 the slot *is* the configuration, so an answer is
 * `{ field, aggregate }` where `field` is the column's **field uuid**. The
 * contract stores the dataset's published `fieldKey` instead, and the
 * dataset's own column list is the only thing that maps one to the other.
 *
 * Why the function above cannot do it: it resolves against `measures` and
 * `groupers`, the Data-step rows the dataset-first flow removed. With no rows
 * to match, every slot came back unanswered and a reopened widget showed an
 * empty Configure tab over bindings that were perfectly good.
 *
 * ## `field`, not `field_key`
 *
 * The two differ exactly where it matters. On a dataset `field` is the
 * published column — `total_amount` — while `field_key` is the key the
 * binding is filed under, which `buildWidgetBindings` derives from the
 * operation and suffixes when another slot has taken it: `total_amount_avg`.
 * Reading `field_key` first therefore missed every measure that names a
 * column, so a reopened `SUM`/`AVG`/`MIN`/`MAX` slot came back empty and the
 * next save wrote over a good binding. `field_key` stays as the fallback for
 * a dimension, which carries both and sets them to the same thing.
 *
 * The row key is skipped on purpose. It is the backend's column, the payload
 * re-emits it from the dataset on every save, and an answer here would count
 * as an author's choice — enough to make `datasetAssignments` treat a record
 * set as grouped.
 *
 * @param {object} params
 * @param {object|null} params.bindings The stored `visualization_schema.bindings`.
 * @param {Array<{fieldUuid: string, fieldKey: string, isRowKey: boolean}>}
 *   [params.columns] The dataset's columns, as `getDataset` answers them.
 * @returns {object} `{ [slotKey]: item | item[] }` for every slot it resolved.
 */
export function restoreDatasetSlotAssignments({ bindings, columns = [] }) {
  if (!bindings || typeof bindings !== 'object' || !columns.length) return EMPTY;

  const byFieldKey = new Map(columns.map((column) => [column.fieldKey, column]));

  const itemFor = (stored) => {
    if (!stored || typeof stored !== 'object') return null;
    const { aggregate = '', label = '' } = stored;
    const withLabel = label ? { label } : {};

    // Counting rows reads no column, and the schema rejects it with one.
    if (aggregate === AGGREGATE_WITHOUT_FIELD) return { aggregate, ...withLabel };

    const column = byFieldKey.get(stored.field || stored.field_key || '');
    if (!column?.fieldUuid || column.isRowKey) return null;
    return { field: column.fieldUuid, ...(aggregate ? { aggregate } : {}), ...withLabel };
  };

  return Object.entries(bindings).reduce((acc, [slot, stored]) => {
    if (NON_FIELD_BINDING_SLOTS.includes(slot) || !stored) return acc;

    if (Array.isArray(stored)) {
      const items = stored.map(itemFor).filter(Boolean);
      return items.length ? { ...acc, [slot]: items } : acc;
    }

    const item = itemFor(stored);
    return item ? { ...acc, [slot]: item } : acc;
  }, EMPTY);
}

export default restoreSlotAssignments;
